import { ColumnDef, sortingFns } from '@tanstack/react-table';
import { versionSortingFn } from 'src/components/GenericTable/sortingFn';
import React, { useMemo, useState } from 'react';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from '../types';
import { Link } from 'react-router-dom';
import { gTCheckHandler } from '../util';
import { MPEvents } from 'src/utils/mixpanel/types';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TextOverflowTooltip } from 'src/common';
import {
  ControlFrameworkDetailsQuery,
  useAddControlsFromLibraryMutation,
  useControlsRmpCheckQuery,
} from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import { useAuth } from 'src/state/auth';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import * as S from './useControlLibraryColumns.styles';

type ControlLibraryItem = ControlFrameworkDetailsQuery['controlFrameworks'][number]['frameworkControls'][number];

type UseColumns = (a: {
  data?: ControlLibraryItem[];
  isCheckerEnabled?: boolean;
  opensSameTab?: boolean;
  tdm?: {
    onAddToRMPClick?: boolean;
  };
  tableId: TableIds;
}) => {
  columns: ColumnDef<ControlLibraryItem>[];
  checked: string[];
  setChecked: (a: string[]) => void;
  GTColumnSelectAnchorExported: JSX.Element;
  addToRmp: (ids: string[]) => Promise<void>;
  isAddLoading: boolean;
};

const defaultColumns = ['framework_controls', 'framework_control_id', 'name', 'FrameworkAuthor'];

export const useControlLibraryColumns: UseColumns = ({ data = [], isCheckerEnabled, tdm, opensSameTab, tableId }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${tableId}-columns`, defaultColumns);
  const [checked, setChecked] = useState<string[]>([]);
  const { refetchQuery } = useCache();
  const { workspaceId } = useAuth();

  const { data: { controls: rmpControls = [] } = {} } = useControlsRmpCheckQuery();
  const [isAddLoading, setIsAddLoading] = useState(false);

  const [addControlsFromLibrary] = useAddControlsFromLibraryMutation();

  const addToRmp = async (ids: string[]) => {
    setIsAddLoading(true);
    await addControlsFromLibrary({
      variables: {
        workspaceId,
        controlIds: ids.map((id) => ({ id })),
      },
    });
    await refetchQuery({ include: ['controlsRmpCheck'] });
    showSuccessToast(ids.length === 1 ? toastMessages.control.addedToRmp : toastMessages.control.addedToRmpPlural);
    setIsAddLoading(false);
  };

  const onAddToRMPClick = async (id: string) => {
    setIsAddLoading(true);
    await addControlsFromLibrary({
      variables: {
        workspaceId,
        controlIds: [{ id }],
        addRecommendedModifiers: false,
      },
    });
    await refetchQuery({ include: ['controlsRmpCheck'] });
    showSuccessToast(toastMessages.control.addedToRmp);
    setIsAddLoading(false);
  };

  const columns = useMemo(() => {
    let internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

    if (isCheckerEnabled) {
      internalIds.push(GTInternalIds.checker);
    } else {
      internalIds.filter((el) => el !== GTInternalIds.checker);
      if (checked.length > 0) setChecked([]);
    }

    const list: ColumnDef<ControlLibraryItem>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const rows = table.getRowModel().rows;
          const headChecked =
            rows
              .map((row) => row.original.id)
              .every((id) =>
                rmpControls.some((item) => (item.frameworkControls || []).some((fc) => fc?.id === id)),
              ) && !!rows.length;

          const { isAllChecked, checkAllHandler } = gTCheckHandler(
            {
              checked,
              selector: 'id',
              table,
            },
            setChecked,
          );

          return (
            <GTCheckBox isChecked={isAllChecked || headChecked} disabled={headChecked} onClick={checkAllHandler} />
          );
        },
        cell: ({ row }) => {
          const id = row.original.id;
          const isInRmp = rmpControls.some((item) =>
            (item.frameworkControls || []).some((fc) => fc?.id === row.original.id),
          );

          return (
            <GTCheckBox
              isChecked={checked.includes(id) || isInRmp}
              disabled={isInRmp}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        accessorFn: (row) => row.controlFramework?.shortname,
        cell: ({ row }) => row.original.controlFramework?.shortname,
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_control_id',
        header: 'Framework Control ID',
        accessorFn: (row) => row.control_identifier,
        cell: ({ row }) => row.original.control_identifier,
        sortingFn: versionSortingFn,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/control-frameworks/all-controls/${info.row.original.id}`}
              target={opensSameTab ? '' : '_blank'}
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['Framework'],
                  },
                  destination: {
                    value: ['Framework Controls all'],
                    params: {
                      id: info.row.original.id,
                    },
                  },
                  tags: ['FRAMEWORK'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'FrameworkAuthor',
        header: 'Author',
        accessorFn: (row) => row.controlFramework?.author,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },

      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const isInRmp = rmpControls.some((item) =>
            (item.frameworkControls || []).some((fc) => fc?.id === row.original.id),
          );
          return (
            <S.TdmActionsRow>
              <GTTdm
                onAddToRMPClick={tdm?.onAddToRMPClick && !isInRmp ? () => onAddToRMPClick(row.original.id) : undefined}
              />
            </S.TdmActionsRow>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, rmpControls, isCheckerEnabled, checked, tdm]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={tableId}
      options={[
        { key: 'framework_controls', title: 'Framework' },
        { key: 'framework_control_id', title: 'Framework Control ID' },
        { key: 'name', title: 'Name' },
        { key: 'FrameworkAuthor', title: 'Author' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, checked, setChecked, GTColumnSelectAnchorExported, addToRmp, isAddLoading };
};
